import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text,
  Divider,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { StyledProfileAvatar } from "./style";
import { CustomText } from "@root/components/common/components";
import { useTranslation } from "react-i18next";
import { isRouteExist } from "@root/utils/helpers";
import useSidebar from "@root/hooks/useSidebar";
import { useOrganizationBranding } from "@root/context/organization-branding-context";
import ArrowLeftIcon from "@root/assets/icons/ArrowLeftIcon.svg";
import { CustomPopup } from "@root/components/common/modals/CustomPopup";
import { LogoutPopupContent } from "@root/pages/components";

interface MobileSideMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileSideMenu({ isOpen, onClose }: MobileSideMenuProps) {
  const user: any = useSelector(getLoggedInUser);
  const { t } = useTranslation("common");
  const { logoUrl, defaultLogoUrl, loading } = useOrganizationBranding();

  const {
    sidebar,
    pathname,
    menuItemsList,
    logoutPopup,
    setLogoutPopup,
    logoutHandler,
  } = useSidebar();

  const handleMenuClick = (menu: any) => {
    menu?.onClick();
    onClose(); // Close the drawer after navigation
  };

  return (
    <>
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="full">
        <DrawerOverlay />
        <DrawerContent
          bgColor={"theme.bg.box"}
          maxW="90%"
          style={{ height: "100dvh", maxHeight: "100dvh" }}
        >
          <DrawerHeader borderBottomWidth="1px" pb="4" bgColor={"#EEEEF9"}>
            <Flex justifyContent="space-between" alignItems="center">
              {loading ? (
                <Spinner size="md" />
              ) : (
                <Image
                  src={logoUrl || defaultLogoUrl}
                  alt="Logo"
                  maxH="38px"
                  objectFit="contain"
                />
              )}
              <Image
                src={ArrowLeftIcon}
                alt="Arrow Left Icon"
                height="30px"
                onClick={onClose}
              />
            </Flex>
          </DrawerHeader>

          <DrawerBody p="0" mx={"12px"} overflowY="auto">
            <Flex
              flexDirection={"column"}
              justifyContent={"space-between"}
              minHeight={"100%"}
            >
              <Box>
                {/* User Profile Section */}
                <Flex
                  alignItems="center"
                  gap="16px"
                  p="16px"
                  borderBottom="1px solid #E6E9EF"
                >
                  <StyledProfileAvatar
                    name={user?.fullName || ""}
                    src={user?.avatar}
                  />
                  <Flex flexDirection="column" gap="4px" flex="1">
                    <CustomText
                      fontSize="16px"
                      fontWeight={500}
                      textColor="#0D0D0D"
                      text={user?.fullName || ""}
                    />
                    <CustomText
                      fontSize="14px"
                      fontWeight={400}
                      textColor="#5C6670"
                      text={user?.email || ""}
                    />
                  </Flex>
                </Flex>
                {/* Navigation Menu Items */}
                <Box p="8px">
                  {sidebar?.map((menu: any, index: number) => {
                    const isActive = isRouteExist(pathname, menu?.routes);
                    const IconComponent = menu?.iconComponent;

                    return (
                      <Flex
                        key={index}
                        alignItems="center"
                        gap="12px"
                        p="12px 16px"
                        borderRadius="8px"
                        cursor="pointer"
                        bg={isActive ? "#1819b4" : "transparent"}
                        color={isActive ? "white" : "#0D0D0D"}
                        onClick={() => handleMenuClick(menu)}
                        _hover={{
                          bg: "#1819b4",
                          color: "white",
                          textColor: "white",
                        }}
                        mb="4px"
                      >
                        {IconComponent && (
                          <Box
                            boxSize={4}
                            color={isActive ? "white" : "#798692"}
                          >
                            <IconComponent />
                          </Box>
                        )}
                        <Text
                          fontSize="16px"
                          fontWeight={isActive ? 500 : 400}
                          color={isActive ? "white" : "#0D0D0D"}
                        >
                          {menu?.text}
                        </Text>
                      </Flex>
                    );
                  })}
                </Box>
              </Box>

              {/* Logout Section */}
              <Box p="8px" borderTop="1px solid #E6E9EF" mt="auto">
                {menuItemsList.map((menu: any, index: number) => {
                  if (menu?.title === "hr") {
                    return <Divider key={index} my="8px" />;
                  }

                  return (
                    <Flex
                      key={index}
                      alignItems="center"
                      gap="12px"
                      p="12px 16px"
                      borderRadius="8px"
                      cursor="pointer"
                      onClick={() => {
                        setLogoutPopup(true);
                      }}
                      _hover={{
                        bg: "#1819b4",
                        color: "white",
                        textColor: "white",
                      }}
                    >
                      {menu?.icon && <Box color="#798692">{menu.icon}</Box>}
                      <Text fontSize="16px" fontWeight={400} color="#0D0D0D">
                        {menu?.title}
                      </Text>
                    </Flex>
                  );
                })}
              </Box>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Logout Popup */}
      {logoutPopup && (
        <CustomPopup
          open={logoutPopup}
          onClose={() => setLogoutPopup(false)}
          title={t("preferences.signOutModalTitle")}
          children={
            <LogoutPopupContent
              onSignOut={logoutHandler}
              onCancel={() => setLogoutPopup(false)}
            />
          }
        />
      )}
    </>
  );
}
